import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useToken } from "./TokenContext";
import "../css/useraccess.css";

/**
 * StudySphere SSO連携用の自動ログインページ
 * 
 * 処理フロー:
 * 1. URLクエリパラメータから `ticket` を取得
 * 2. StudySphere APIにチケット検証リクエストを送信
 * 3. 検証成功時にユーザー情報を取得
 * 4. findjob側のセッションを確立（TokenContextとAuthContextを使用）
 * 5. 適切なページにリダイレクト
 */
const AutoLoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setLoggedInToken, setLoggedInUser, setUserName, setUserRole, setCompanyName, setLocationNames } = useToken();
  
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const hasProcessedRef = useRef(false); // 処理済みフラグ（重複実行を防ぐ）

  // StudySphere API URLの取得（環境変数またはデフォルト値）
  const getStudySphereApiUrl = () => {
    // 環境変数から取得を試みる
    const envUrl = process.env.REACT_APP_STUDYSPHERE_API_URL;
    if (envUrl) {
      console.log('AutoLoginPage: 環境変数からAPI URLを取得', envUrl);
      return envUrl.trim();
    }

    // ホスト名から環境を判定
    const hostname = window.location.hostname || '';
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname) || hostname.endsWith('.local');
    
    // 本番環境の判定: NODE_ENVまたはホスト名で判定
    const isProductionByEnv = process.env.NODE_ENV === 'production';
    const isProductionByHost = hostname.includes('studysphere') || hostname.includes('ayatori-inc.co.jp');
    const isProduction = isProductionByEnv || isProductionByHost;

    // デバッグログ
    console.log('AutoLoginPage: 環境判定', {
      hostname,
      isLocalhost,
      NODE_ENV: process.env.NODE_ENV,
      isProductionByEnv,
      isProductionByHost,
      isProduction
    });

    // ローカルホストの場合は開発環境のURLを使用
    if (isLocalhost) {
      console.log('AutoLoginPage: ローカル環境を検出、開発API URLを使用');
      return 'http://localhost:5050';
    }

    // 本番環境の場合は本番API URLを使用、それ以外も本番URLをデフォルトとする（セキュリティのため）
    const apiUrl = isProduction 
      ? 'https://backend.studysphere.ayatori-inc.co.jp'
      : 'https://backend.studysphere.ayatori-inc.co.jp'; // デフォルトは本番環境
    
    console.log('AutoLoginPage: API URLを決定', apiUrl);
    return apiUrl;
  };

  // チケット検証API呼び出し
  const verifyTicket = async (ticket) => {
    const apiBaseUrl = getStudySphereApiUrl();
    const verifyUrl = `${apiBaseUrl}/api/sso/ticket/verify`;

    try {
      const response = await fetch(verifyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ticket }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.valid) {
        return {
          success: false,
          error: data.error || "INVALID_TICKET",
          message: data.message || "チケットの検証に失敗しました",
          source_system: data.source_system || null
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("チケット検証エラー:", error);
      return {
        success: false,
        error: "VERIFICATION_ERROR",
        message: error.message || "サーバーエラーが発生しました",
      };
    }
  };

  // 自動ログイン処理
  useEffect(() => {
    // 既に処理済みの場合はスキップ（React StrictModeや再レンダリングによる重複実行を防ぐ）
    if (hasProcessedRef.current) {
      console.log("AutoLoginPage: 既に処理済みのためスキップ");
      return;
    }

    // 処理中でない場合もスキップ（エラーまたは成功状態）
    if (status !== "processing") {
      console.log("AutoLoginPage: 既に処理完了のためスキップ", { status });
      return;
    }

    const handleAutoLogin = async () => {
      // 処理開始フラグを設定
      hasProcessedRef.current = true;

      try {
        // クエリパラメータからticketを取得
        const ticket = searchParams.get("ticket");

        if (!ticket) {
          setStatus("error");
          setErrorMessage("チケットパラメータが提供されていません");
          console.error("AutoLoginPage: ticketパラメータが見つかりません");
          return;
        }

        console.log("AutoLoginPage: チケット検証を開始", { ticket: ticket.substring(0, 10) + "..." });

        // チケット検証
        const verificationResult = await verifyTicket(ticket);

        if (!verificationResult.success) {
          // NO_LOGIN_CODEエラーの場合、元システムにリダイレクト
          if (verificationResult.error === "NO_LOGIN_CODE" && verificationResult.source_system) {
            const sourceSystem = verificationResult.source_system;
            const redirectUrl = `${sourceSystem.base_url}${sourceSystem.landing_path}`;
            
            console.log("AutoLoginPage: ログインコード未所持のため元システムにリダイレクト", {
              sourceSystem: sourceSystem.system_key,
              redirectUrl
            });
            
            // 元システムにリダイレクト（即座に実行）
            window.location.href = redirectUrl;
            return;
          }
          
          setStatus("error");
          setErrorMessage(verificationResult.message || "チケットの検証に失敗しました");
          console.error("AutoLoginPage: チケット検証失敗", verificationResult);
          
          // エラー表示後、3秒後にユーザーアクセスページへリダイレクト
          setTimeout(() => {
            navigate("/useraccess");
          }, 3000);
          return;
        }

        const userData = verificationResult.data;
        console.log("AutoLoginPage: チケット検証成功", {
          userId: userData.user_id,
          username: userData.username,
          name: userData.name,
          role: userData.role,
          companyName: userData.company_name,
          locationNames: userData.locationNames,
          expiresAt: userData.expiresAt,
        });

        // findjob側のセッションを確立
        // 有効期限を設定（APIから取得した有効期限を使用、なければ24時間後）
        let expiresAt;
        if (userData.expiresAt) {
          expiresAt = new Date(userData.expiresAt);
        } else {
          expiresAt = new Date();
          expiresAt.setHours(expiresAt.getHours() + 24);
        }
        
        // userData.usernameはlogin_code（通常のトークン形式）なので、それをloggedInTokenとして使用
        // これにより、適職診断などのLambda関数で通常のトークン形式として認識される
        const loginCode = userData.username || `sso-${userData.user_id}`;
        
        // localStorageに保存
        localStorage.setItem("accessExpiry", expiresAt.getTime());
        localStorage.setItem("userAffiliation", userData.company_id?.toString() || userData.username || "");
        localStorage.setItem("loggedInToken", loginCode); // login_codeを保存（適職診断で使用可能）
        localStorage.setItem("userName", userData.name || "");
        localStorage.setItem("userRole", userData.role?.toString() || "1");
        localStorage.setItem("companyName_findjob", userData.company_name || "StudySphere連携");
        if (userData.locationNames && userData.locationNames.length > 0) {
          localStorage.setItem("locationNames", JSON.stringify(userData.locationNames));
        }

        // Contextに設定
        setLoggedInUser(userData.username || "");
        setLoggedInToken(loginCode); // login_codeを設定（適職診断で使用可能）
        setUserName(userData.name || "");
        setUserRole(Number(userData.role) || 1); // 数値として設定
        setCompanyName(userData.company_name || "StudySphere連携");
        if (userData.locationNames && userData.locationNames.length > 0) {
          setLocationNames(userData.locationNames);
        }
        login();

        setStatus("success");
        console.log("AutoLoginPage: セッション確立完了");

        // ロール4以上（指導員以上）の場合は管理者ページへリダイレクト
        const userRole = Number(userData.role) || 1;
        if (userRole >= 4) {
          console.log("AutoLoginPage: ロール4以上を検出、管理者ページへリダイレクト", { userRole });
          navigate("/Personality-check-4-admin", { replace: true });
        } else {
          // ロール4未満の場合は通常のnameinputページへリダイレクト
          console.log("AutoLoginPage: 通常ユーザーとしてnameinputページへリダイレクト", { userRole });
          navigate("/nameinput", { replace: true });
        }
      } catch (error) {
        console.error("AutoLoginPage: 予期しないエラー", error);
        setStatus("error");
        setErrorMessage("予期しないエラーが発生しました");
        
        setTimeout(() => {
          navigate("/useraccess", { replace: true });
        }, 3000);
      }
    };

    handleAutoLogin();
  }, [searchParams, navigate, login, setLoggedInToken, setLoggedInUser, setUserName, setUserRole, setCompanyName, setLocationNames, status]);

  // ローディング表示
  if (status === "processing") {
    return (
      <div className="container">
        <div className="card">
          <div className="card-header">自動ログイン処理中...</div>
          <div className="card-content">
            <p>StudySphereからの認証情報を確認しています。しばらくお待ちください。</p>
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <div className="spinner" style={{ 
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3498db",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                animation: "spin 1s linear infinite",
                margin: "0 auto"
              }}></div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // エラー表示
  if (status === "error") {
    return (
      <div className="container">
        <div className="card">
          <div className="card-header" style={{ color: "#e74c3c" }}>ログイン失敗</div>
          <div className="card-content">
            <p style={{ color: "#e74c3c" }}>{errorMessage}</p>
            <p style={{ marginTop: "20px", fontSize: "14px", color: "#7f8c8d" }}>
              3秒後にユーザーアクセスページへリダイレクトします...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 成功表示（通常はすぐにリダイレクトされるため、ほとんど表示されない）
  return (
    <div className="container">
      <div className="card">
        <div className="card-header" style={{ color: "#27ae60" }}>ログイン成功</div>
        <div className="card-content">
          <p style={{ color: "#27ae60" }}>認証に成功しました。リダイレクトしています...</p>
        </div>
      </div>
    </div>
  );
};

export default AutoLoginPage;

